import { useEffect } from 'react';
import { isAlive } from 'mobx-state-tree';
import * as Sentry from '@sentry/react';

import { useStore } from 'hooks';
import api from 'services/API';
import { CONDITION_OFFLINE_TYPES } from 'config/alarmsType';

const useEstablishmentData = (onSuccess, onError) => {
  const {
    ui,
    barsStore,
    beveragesStore,
    containersStore,
    itemsStore,
    linesStore,
    tapsStore,
    lineTapsStore,
    itemLinesStore,
    skuStore,
    establishmentStore,
    userStore,
    unitStore,
    coolersStore,
    businessHoursStore,
    roleUserStore,
    itemEventsStore,
    integrationsStore,
    cleaningsStore,
    cleaningStatisticsStore,
    usersStore,
    skuMappingStore,
    menusStore,
    menuDesignsStore,
    menuListsStore,
    reportStore,
    barsRoleUserStore,
    deviceHistoryStore,
    topologyManagementStore,
    notificationsStore,
    conditionsStore,
    subscriptionsStore,
    alarmsStore,
    distributorsStore,
    hardwareStatusStore,
  } = useStore();

  // whenever we change establishment all state gets flushed except for the userStore (see details in /models/root.js)
  // during switch we set new auth_token and currentRole in userStore along with updating ACL rules
  // Change to ACL rules triggers recalculation of available routes for this specific role in routes/index.js
  // and sets a new key on a Fragment component which completely restructures the routes tree
  // which leads to full remount of component tree including the Root component
  // whenever root component is remounted it triggers this hook with up-to-date auth_token (as it was not cleared between switches)
  // having any dependencies in the useEffect below will additionally trigger the hook resulting in extra requests being sent

  useEffect(() => {
    let ignoreRequestResults = false;

    (async () => {
      try {
        if (!userStore.auth_token) return;
        await Promise.all([
          api.getBundle({ number: 1 }).then(res => res.data.result),
          api.getBundle({ number: 2 }).then(res => res.data.result),
          api.getBundle({ number: 3 }).then(res => res.data.result),
        ]).then(response => {
          if (!ignoreRequestResults) {
            const {
              alarms,
              brus,
              sensors,
              line_sensors,
              bars,
              beverages,
              containers,
              lines,
              taps,
              line_taps,
              item_lines,
              skus,
              establishments,
              items_statistics,
              units,
              coolers,
              get_cleaning_statistics,
              pos_skus_active,
              roles,
              users,
              business_hours,
              cleanings,
              integrations,
              role_users,
              menus,
              menu_designs,
              menu_lists,
              report_configurations,
              bars_role_users,
              gateways,
              conditions,
              temperature_sensors,
              establishments_sensors,
              audits_items,
              user_devices,
              subscriptions,
              notifications,
              distributors,
              delivery_schedules,
              notification_preferences,
            } = response.reduce((result, obj) => {
              for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                  result[key] = obj[key];
                }
              }
              return result;
            }, {});

            if (isAlive(establishmentStore)) {
              if (
                !establishments?.rows[0]?.id ||
                userStore?.currentRole?._establishment_id !== establishments?.rows[0]?.id
              ) {
                userStore.changeEstablishment({
                  _roleuser_id: userStore?.profile?.default_roleuser_id,
                });
              }

              if (Array.isArray(establishments?.rows) && establishments?.rows[0]) {
                establishmentStore.setEstablishment(establishments?.rows[0]);
                userStore.setRoles(role_users.rows);
              }
            }

            if (isAlive(containersStore)) {
              containers &&
                containers.rows &&
                Array.isArray(containers.rows) &&
                containersStore.setContainers(containers.rows);
            }

            if (isAlive(beveragesStore)) {
              beverages &&
                beverages.rows &&
                Array.isArray(beverages.rows) &&
                beveragesStore.setBeverages(beverages.rows);
            }

            if (isAlive(itemsStore)) {
              items_statistics &&
                items_statistics.rows &&
                Array.isArray(items_statistics?.rows) &&
                itemsStore.setItems(items_statistics.rows);
            }

            if (isAlive(lineTapsStore)) {
              line_taps &&
                line_taps.rows &&
                Array.isArray(line_taps.rows) &&
                lineTapsStore.setLineTaps(line_taps.rows);
            }

            if (isAlive(linesStore)) {
              lines && lines.rows && Array.isArray(lines.rows) && linesStore.setLines(lines.rows);
            }

            if (isAlive(tapsStore)) {
              taps && taps.rows && Array.isArray(taps.rows) && tapsStore.setTaps(taps.rows);
            }

            if (isAlive(itemLinesStore)) {
              item_lines &&
                item_lines.rows &&
                Array.isArray(item_lines.rows) &&
                itemLinesStore.setItemLines(item_lines.rows);
            }

            if (isAlive(skuStore)) {
              skus && skus.rows && Array.isArray(skus.rows) && skuStore.setSKUs(skus.rows);
            }

            if (isAlive(coolersStore)) {
              coolers &&
                coolers &&
                Array.isArray(coolers.rows) &&
                coolersStore.setCoolers(coolers.rows);
            }

            if (isAlive(unitStore)) {
              units && units.rows && Array.isArray(units.rows) && unitStore.setUnits(units.rows);
            }

            if (isAlive(barsStore)) {
              bars && bars.rows && Array.isArray(bars.rows) && barsStore.setBars(bars.rows);
            }

            if (isAlive(barsRoleUserStore)) {
              bars_role_users &&
                bars_role_users.rows &&
                Array.isArray(bars_role_users.rows) &&
                barsRoleUserStore.setBarsRoleUser(bars_role_users.rows);
            }

            if (isAlive(cleaningStatisticsStore)) {
              get_cleaning_statistics &&
                get_cleaning_statistics.rows &&
                Array.isArray(get_cleaning_statistics.rows) &&
                cleaningStatisticsStore.setCleaningStatistics(get_cleaning_statistics.rows);
            }

            if (isAlive(reportStore)) {
              report_configurations &&
                report_configurations.rows &&
                Array.isArray(report_configurations.rows) &&
                reportStore.setReportConfiguration(report_configurations.rows[0]);
            }

            if (isAlive(skuMappingStore)) {
              Array.isArray(pos_skus_active?.rows) &&
                skuMappingStore.setActivePOSSKUs(pos_skus_active.rows);
            }

            if (isAlive(usersStore)) {
              users && users.rows && Array.isArray(users.rows) && usersStore.setUsers(users.rows);
              roles && roles.rows && Array.isArray(roles.rows) && usersStore.setRoles(roles.rows);
            }

            if (isAlive(businessHoursStore)) {
              business_hours &&
                business_hours.rows &&
                Array.isArray(business_hours.rows) &&
                businessHoursStore.setBusinessHours(business_hours.rows);
            }

            if (isAlive(cleaningsStore)) {
              cleanings &&
                cleanings.rows &&
                Array.isArray(cleanings.rows) &&
                cleaningsStore.setCleanings(cleanings.rows);
            }

            if (isAlive(integrationsStore)) {
              integrations &&
                integrations.rows &&
                Array.isArray(integrations.rows) &&
                integrationsStore.setIntegrations(integrations.rows);
            }

            if (isAlive(itemEventsStore)) {
              audits_items &&
                audits_items.rows &&
                Array.isArray(audits_items.rows) &&
                itemEventsStore.setItemEvents(audits_items.rows);
            }

            if (isAlive(notificationsStore)) {
              if (Array.isArray(user_devices?.rows)) {
                notificationsStore.setUserDevices(user_devices.rows);
                notificationsStore.checkCurrentSubscription();
              }

              Array.isArray(alarms?.rows) && notificationsStore.setAlarms(alarms.rows);
              Array.isArray(conditions?.rows) && conditionsStore.setConditions(conditions.rows);
              Array.isArray(notifications?.rows) &&
                notificationsStore.setNotifications(notifications.rows);
              Array.isArray(notification_preferences?.rows) &&
                notificationsStore.setNotificationPreferences(notification_preferences.rows);
            }

            if (isAlive(alarmsStore)) {
              Array.isArray(alarms?.rows) && alarmsStore.setAlarms(alarms.rows);
            }

            if (isAlive(deviceHistoryStore)) {
              Array.isArray(gateways?.rows) && deviceHistoryStore.setDeviceHistory(gateways.rows);
            }

            if (isAlive(topologyManagementStore)) {
              Array.isArray(establishments_sensors?.rows) &&
                topologyManagementStore.setEstablishmentSensors(establishments_sensors.rows[0]);

              Array.isArray(brus?.rows) && topologyManagementStore.setBRUs(brus.rows);

              Array.isArray(gateways?.rows) && topologyManagementStore.setGateways(gateways.rows);

              Array.isArray(line_sensors?.rows) &&
                topologyManagementStore.setLineSensors(line_sensors.rows);

              Array.isArray(sensors?.rows) && topologyManagementStore.setSensors(sensors.rows);
              Array.isArray(temperature_sensors?.rows) &&
                topologyManagementStore.setTemperatureSensors(temperature_sensors.rows);

              topologyManagementStore.setIsLoaded(true);
            }

            if (isAlive(roleUserStore)) {
              role_users &&
                role_users.rows &&
                Array.isArray(role_users.rows) &&
                roleUserStore.setRoleUsers(role_users.rows);
            }

            if (isAlive(menusStore)) {
              menus &&
                menus.rows &&
                Array.isArray(menus.rows) &&
                menusStore.setDigitalMenus(menus.rows);
            }

            if (isAlive(menuDesignsStore)) {
              menu_designs &&
                menu_designs.rows &&
                Array.isArray(menu_designs.rows) &&
                menuDesignsStore.setMenuDesigns(menu_designs.rows);
            }

            if (isAlive(menuListsStore)) {
              menu_lists &&
                menu_lists.rows &&
                Array.isArray(menu_lists.rows) &&
                menuListsStore.setMenuLists(menu_lists.rows);
            }

            if (isAlive(subscriptionsStore)) {
              subscriptions &&
                subscriptions?.rows &&
                Array.isArray(subscriptions.rows) &&
                subscriptionsStore.setSubscriptions(subscriptions.rows);
            }

            if (isAlive(distributorsStore)) {
              distributors &&
                distributors.rows &&
                Array.isArray(distributors.rows) &&
                distributorsStore.setDistributors(distributors.rows);
              delivery_schedules &&
                delivery_schedules.rows &&
                Array.isArray(delivery_schedules.rows) &&
                distributorsStore.setDistributorsSchedules(delivery_schedules.rows);
            }

            if (isAlive(hardwareStatusStore)) {
              Array.isArray(conditions?.rows) &&
                hardwareStatusStore.setOfflineConditions(
                  conditions.rows.filter(
                    condition =>
                      CONDITION_OFFLINE_TYPES.includes(condition.type) &&
                      condition.happened_to === null,
                  ),
                );
            }
            if (isAlive(ui)) {
              ui.setIsDataLoaded(true);
              ui.setIsDataLoading(false);
            }

            if (typeof onSuccess === 'function') {
              onSuccess();
            }
          }
        });
      } catch (err) {
        console.error(err);
        if (isAlive(ui)) {
          ui.setIsDataLoading(false);
        }
        Sentry.captureException(new Error('Failed to load establishment data')); // to cover the case when core establishment data is not loaded and user is not able to use the app at all
        if (typeof onError === 'function') {
          onError(err);
        }
        if (isAlive(userStore)) {
          userStore.logout(false);
        }
      }
    })();

    return () => {
      ignoreRequestResults = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useEstablishmentData;
